import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import './Login.css'; // Import the new CSS file
import logo from '../assets/images/light/Logo.png';
import Footer from './Footer'; // Import the shared Footer component

const API_URL = process.env.REACT_APP_API_URL;

function Login() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    // Clear any admin-related localStorage values when login page loads
    localStorage.removeItem('is_admin');
    
    // Force setting is_admin to false to overwrite any cached values
    localStorage.setItem('is_admin', 'false');
    
    // Check if user is already logged in
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      setToken(storedToken);
      const payload = parseJwt(storedToken);
      setUser({
        email: payload.email,
        name: payload.name,
        picture: payload.picture,
      });
      navigate('/home');
    }
  }, [navigate]);

  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error parsing JWT:', e);
      return {};
    }
  };

  const handleLoginSuccess = async (credentialResponse) => {
    try {
      // Clear any existing admin status before login
      localStorage.removeItem('is_admin');
      
      console.log('Attempting to connect to:', `${API_URL}/auth/google`);
      const response = await axios.post(`${API_URL}/auth/google`, {
        credential: credentialResponse.credential,
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        withCredentials: true
      });

      // Store only essential authentication information
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user_email', response.data.user.email);
      if (response.data.user.lastLogin) {
        localStorage.setItem('last_login', response.data.user.lastLogin);
      }
      
      // Explicitly set is_admin to false
      localStorage.setItem('is_admin', 'false');
      
      setToken(response.data.token);
      setUser(response.data.user);
      setError('');

      navigate('/home');
    } catch (error) {
      console.error('Login error details:', {
        message: error.message,
        code: error.code,
        request: error.request ? {
          url: error.request.url,
          method: error.request.method,
          status: error.request.status,
          headers: error.request.headers
        } : 'No request object',
        response: error.response ? {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        } : 'No response received'
      });
      
      let errorMessage = 'An unexpected error occurred during login.';
      
      if (error.response) {
        // Server responded with an error
        const serverError = error.response.data.error;
        const details = error.response.data.details;
        
        if (serverError === 'Access denied: Only vaidhyamegha.com users are allowed') {
          errorMessage = 'Authentication failed: Only @vaidhyamegha.com email addresses are allowed.';
        } else if (serverError === 'Invalid Google token') {
          errorMessage = 'Google authentication failed. Please try signing in again.';
        } else if (serverError === 'Database error') {
          errorMessage = 'Server database error. Please try again later.';
        } else {
          errorMessage = serverError || 'Server error: ' + (details || 'Unknown error');
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = `Unable to reach the server at ${API_URL}. Please ensure:\n` +
                      '1. The server is accessible\n' +
                      '2. No firewall or CORS policy is blocking the connection\n' +
                      '3. The server URL is correct';
        console.error('Connection failed to:', API_URL);
      } else {
        // Error in request setup
        errorMessage = 'Error setting up authentication request: ' + error.message;
      }
      
      setError(errorMessage);
    }
  };

  const handleLoginError = (error) => {
    console.error('Google login error:', error);
    setError('Google Sign-In failed. Please try again or contact support if the issue persists.');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-info">
          <img src={logo} alt="VaidhyaMegha Logo" className="logo" />
          <div className="heading-group">
            <h1 className="welcome-text">Welcome to</h1>
            <h1 className="brand-text">DevOps Buddy</h1>
          </div>
          <p>Your all-in-one dashboard to monitor, automate, and manage DevOps operations — securely and smartly.</p>
        </div>
        <div className="login-form-container">
          <h2>Sign in with <strong>Google</strong></h2>
          <GoogleLogin
            onSuccess={handleLoginSuccess}
            onError={handleLoginError}
            useOneTap
            theme="outline"
            size="large"
            width="300px"
          />
          {error && (
            <p style={{ color: 'red', marginTop: '20px', whiteSpace: 'pre-wrap' }}>
              {error}
            </p>
          )}
        </div>
      </div>
      <Footer onLoginPage={true} />
    </div>
  );
}

export default Login;
